import { Controller } from "@hotwired/stimulus"

interface FieldState {
  element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  originalValue: string
  originalChecked?: boolean
}

/**
 * Stimulus controller for UnsavedChangesModalComponent.
 * Tracks form field changes and intercepts navigation when unsaved changes exist.
 *
 * Behavior:
 * - For both "new" and "edit" forms: shows modal only when form has unsaved changes
 * - Clean edit forms navigate freely without showing the modal
 *
 * Intercepts:
 * - Turbo visits (turbo:before-visit)
 * - Turbo Stream requests (turbo:before-fetch-request) - for links with data-turbo-stream
 * - Link clicks within the form wrapper
 *
 * Usage:
 *   data-controller="application--toolkit--unsaved-changes-modal-component--unsaved-changes-modal-component"
 *   data-...-modal-id-value="unique-id"
 *   data-...-form-id-value="form-element-id"
 *   data-...-is-edit-value="true|false"
 */
export default class extends Controller {
  static targets = ["overlay", "formWrapper"]
  static values = {
    modalId: String,
    formId: String,
    isEdit: Boolean,
    // URL pattern to allow through without modal (e.g., "/choice_groups" for edit/new actions)
    allowedUrlPattern: String
  }

  declare overlayTarget: HTMLElement
  declare hasOverlayTarget: boolean
  declare formWrapperTarget: HTMLElement
  declare hasFormWrapperTarget: boolean
  declare modalIdValue: string
  declare formIdValue: string
  declare isEditValue: boolean
  declare allowedUrlPatternValue: string
  declare hasAllowedUrlPatternValue: boolean

  private boundKeyHandler: ((event: KeyboardEvent) => void) | null = null
  private boundTurboVisitHandler: ((event: Event) => void) | null = null
  private boundTurboFetchHandler: ((event: Event) => void) | null = null
  private boundNavigateHandler: ((event: Event) => void) | null = null
  private fieldStates: FieldState[] = []
  private pendingNavigationUrl: string | null = null
  private pendingFetchEvent: Event | null = null
  private formElement: HTMLFormElement | null = null
  private isNavigating: boolean = false
  private boundTurboSubmitEndHandler: ((event: Event) => void) | null = null
  private pendingIsTurboStream: boolean = false
  private postSaveUrl: string | null = null

  connect(): void {
    this.boundKeyHandler = this.handleKeydown.bind(this)
    this.boundTurboVisitHandler = this.handleTurboBeforeVisit.bind(this)
    this.boundTurboFetchHandler = this.handleTurboBeforeFetch.bind(this)
    this.boundTurboSubmitEndHandler = this.handleTurboSubmitEnd.bind(this)

    // Find the form element
    this.formElement = document.getElementById(this.formIdValue) as HTMLFormElement
    if (!this.formElement) {
      console.warn(`UnsavedChangesModal: Form with id "${this.formIdValue}" not found`)
      return
    }

    // Initialize field tracking
    this.initializeFieldStates()

    // Listen for Turbo navigation (regular visits)
    document.addEventListener("turbo:before-visit", this.boundTurboVisitHandler)

    // Listen for Turbo Stream requests (links with data-turbo-stream)
    document.addEventListener("turbo:before-fetch-request", this.boundTurboFetchHandler)

    // Listen for Turbo form submission end
    document.addEventListener("turbo:submit-end", this.boundTurboSubmitEndHandler)

    // Listen for explicit navigate requests from parent controllers (e.g., Back to Ballot Design)
    this.boundNavigateHandler = this.handleNavigateRequest.bind(this)
    document.addEventListener("unsaved-changes:navigate", this.boundNavigateHandler)

    // Intercept link clicks within the wrapper
    this.interceptLinkClicks()
  }

  disconnect(): void {
    this.removeKeyboardListener()

    if (this.boundTurboVisitHandler) {
      document.removeEventListener("turbo:before-visit", this.boundTurboVisitHandler)
    }

    if (this.boundTurboFetchHandler) {
      document.removeEventListener("turbo:before-fetch-request", this.boundTurboFetchHandler)
    }

    if (this.boundTurboSubmitEndHandler) {
      document.removeEventListener("turbo:submit-end", this.boundTurboSubmitEndHandler)
    }

    if (this.boundNavigateHandler) {
      document.removeEventListener("unsaved-changes:navigate", this.boundNavigateHandler)
    }

    this.boundKeyHandler = null
    this.boundTurboVisitHandler = null
    this.boundTurboFetchHandler = null
    this.boundTurboSubmitEndHandler = null
    this.boundNavigateHandler = null
    this.fieldStates = []
  }

  /**
   * Initialize tracking for all form fields
   */
  private initializeFieldStates(): void {
    if (!this.formElement) return

    const fields = this.formElement.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "input, textarea, select"
    )

    this.fieldStates = Array.from(fields).map((element) => {
      const state: FieldState = {
        element,
        originalValue: element.value
      }

      if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio")) {
        state.originalChecked = element.checked
      }

      return state
    })
  }

  /**
   * Check if form has unsaved changes
   */
  private isDirty(): boolean {
    return this.fieldStates.some((state) => {
      const { element, originalValue, originalChecked } = state

      if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio")) {
        return element.checked !== originalChecked
      }

      return element.value !== originalValue
    })
  }

  /**
   * Determine if modal should be shown based on form state and mode
   */
  private shouldShowModal(): boolean {
    if (!this.isFormVisible()) return false
    return this.isDirty()
  }

  /**
   * Check if the tracked form element is visible in the DOM
   */
  private isFormVisible(): boolean {
    if (!this.formElement) return false
    return this.formElement.offsetParent !== null
  }

  /**
   * Handle explicit navigate requests dispatched by parent controllers.
   * Used by the management controller's "Back to Ballot Design" action and
   * by selectItem for click-to-switch editing.
   * Accepts an optional `turboStream` flag: when true and form is clean,
   * fetches the URL as a turbo-stream instead of doing a full Turbo.visit.
   */
  private handleNavigateRequest(event: Event): void {
    const customEvent = event as CustomEvent<{ url: string; turboStream?: boolean }>
    const url = customEvent.detail?.url
    if (!url) return

    const isTurboStream = customEvent.detail?.turboStream ?? false

    if (this.shouldShowModal()) {
      this.pendingNavigationUrl = url
      this.pendingIsTurboStream = isTurboStream
      this.open()
    } else {
      // No unsaved changes — navigate directly
      if (isTurboStream) {
        fetch(url, {
          method: "GET",
          headers: {
            "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
          },
          credentials: "same-origin"
        })
          .then(response => response.text())
          .then(html => {
            if (typeof window.Turbo !== "undefined" && window.Turbo.renderStreamMessage) {
              window.Turbo.renderStreamMessage(html)
            }
          })
          .catch(error => console.error("Failed to fetch turbo stream:", error))
      } else if (typeof window.Turbo !== 'undefined' && window.Turbo.visit) {
        window.Turbo.visit(url)
      } else {
        window.location.href = url
      }
    }
  }

  /**
   * Intercept link clicks within the form wrapper
   */
  private interceptLinkClicks(): void {
    // Attach mode: scope to the form element directly (no formWrapper target)
    // Wrap mode: scope to the formWrapper target
    const scope = this.hasFormWrapperTarget
      ? this.formWrapperTarget
      : this.formElement

    if (!scope) return

    scope.addEventListener("click", (event: Event) => {
      const target = event.target as HTMLElement
      const link = target.closest("a[href]") as HTMLAnchorElement | null

      if (!link) return

      // Don't intercept form submit buttons or anchor links
      const href = link.getAttribute("href")
      if (!href || href.startsWith("#")) return

      // Don't intercept if it's a download link
      if (link.hasAttribute("download")) return

      // Check if we should show modal
      if (this.shouldShowModal()) {
        event.preventDefault()
        event.stopPropagation()
        this.pendingNavigationUrl = href
        this.open()
      }
    })
  }

  /**
   * Handle Turbo navigation attempts
   */
  private handleTurboBeforeVisit(event: Event): void {
    // Skip if we're already navigating (user chose to discard)
    if (this.isNavigating) return

    const turboEvent = event as CustomEvent<{ url: string }>

    // Don't intercept if modal is already open
    if (this.hasOverlayTarget && !this.overlayTarget.hasAttribute("hidden")) {
      return
    }

    // Check if we should show modal
    if (this.shouldShowModal()) {
      event.preventDefault()
      this.pendingNavigationUrl = turboEvent.detail.url
      this.open()
    }
  }

  /**
   * Handle Turbo Stream fetch requests (for links with data-turbo-stream)
   * This catches Edit buttons and other Turbo Stream navigation
   */
  private handleTurboBeforeFetch(event: Event): void {
    // Skip if we're already navigating (user chose to discard)
    if (this.isNavigating) return

    const fetchEvent = event as CustomEvent<{ url: URL; fetchOptions: RequestInit }>

    // Don't intercept if modal is already open
    if (this.hasOverlayTarget && !this.overlayTarget.hasAttribute("hidden")) {
      return
    }

    // Only intercept GET requests (not form submissions)
    const method = fetchEvent.detail.fetchOptions.method?.toUpperCase() || "GET"
    if (method !== "GET") return

    // Don't intercept requests from our own form submission
    const targetUrl = fetchEvent.detail.url.toString()
    if (this.formElement) {
      const formAction = this.formElement.action
      if (targetUrl === formAction) return
    }

    // Check if this is a Turbo Stream or Turbo Frame request
    // Turbo Stream uses "text/vnd.turbo-stream.html" in Accept header
    // Turbo Frame requests also update page content
    const headers = fetchEvent.detail.fetchOptions.headers
    let acceptHeader = ""

    if (headers instanceof Headers) {
      acceptHeader = headers.get("Accept") || ""
    } else if (headers && typeof headers === "object") {
      // Handle plain object headers
      const headerObj = headers as Record<string, string>
      acceptHeader = headerObj["Accept"] || headerObj["accept"] || ""
    }

    const isTurboStream = acceptHeader.includes("text/vnd.turbo-stream.html")
    const isTurboFrame = acceptHeader.includes("text/html") && fetchEvent.detail.fetchOptions.headers

    // Intercept turbo-stream requests that would change form content
    // These are typically Edit/navigation links with data-turbo-stream="true"
    if (!isTurboStream && !isTurboFrame) return

    // If an allowed URL pattern is set, skip interception for matching URLs
    // This allows edit/new actions within the same controller to proceed without modal
    if (this.hasAllowedUrlPatternValue && this.allowedUrlPatternValue) {
      if (targetUrl.includes(this.allowedUrlPatternValue)) {
        // Allow this request through - it's switching between forms, not navigating away
        return
      }
    }

    // Check if we should show modal
    if (this.shouldShowModal()) {
      event.preventDefault()
      this.pendingNavigationUrl = targetUrl
      this.pendingIsTurboStream = isTurboStream
      this.open()
    }
  }

  /**
   * Open the modal
   */
  open(): void {
    if (this.hasOverlayTarget) {
      this.overlayTarget.removeAttribute("hidden")
      this.overlayTarget.setAttribute("aria-hidden", "false")
      document.body.style.overflow = "hidden"
      this.addKeyboardListener()

      // Focus the cancel button for accessibility
      const cancelButton = this.overlayTarget.querySelector<HTMLButtonElement>(
        '[data-action*="cancel"]'
      )
      if (cancelButton) {
        setTimeout(() => cancelButton.focus(), 100)
      }
    }
  }

  /**
   * Close the modal without navigation
   */
  close(): void {
    if (this.hasOverlayTarget) {
      this.overlayTarget.setAttribute("hidden", "")
      this.overlayTarget.setAttribute("aria-hidden", "true")
      document.body.style.overflow = ""
      this.removeKeyboardListener()
    }
    this.pendingNavigationUrl = null
    this.pendingIsTurboStream = false
  }

  /**
   * Cancel - stay in form, close modal
   */
  cancel(event?: Event): void {
    if (event) {
      event.preventDefault()
    }
    this.close()
  }

  /**
   * Discard - navigate away without saving
   */
  discard(event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    // Mark as navigating to prevent re-triggering
    this.isNavigating = true

    // Temporarily disable the handlers to allow navigation
    this.removeAllTurboListeners()

    // Navigate to pending URL
    if (this.pendingNavigationUrl) {
      // If the original request was a Turbo Stream, we need to re-fetch it as a stream
      if (this.pendingIsTurboStream) {
        fetch(this.pendingNavigationUrl, {
          method: "GET",
          headers: {
            "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
            "Turbo-Frame": "_top"
          },
          credentials: "same-origin"
        }).then(response => {
          if (response.ok) {
            return response.text()
          }
          throw new Error("Network response was not ok")
        }).then(html => {
          // Render Turbo Stream response
          if (typeof window.Turbo !== "undefined" && window.Turbo.renderStreamMessage) {
            window.Turbo.renderStreamMessage(html)
          }
        }).catch(error => {
          console.error("Failed to fetch turbo stream:", error)
          // Fallback to regular navigation
          if (typeof window.Turbo !== "undefined" && window.Turbo.visit) {
            window.Turbo.visit(this.pendingNavigationUrl!)
          } else {
            window.location.href = this.pendingNavigationUrl!
          }
        })
      } else {
        // Use Turbo.visit if available for regular navigation
        if (typeof window.Turbo !== "undefined" && window.Turbo.visit) {
          window.Turbo.visit(this.pendingNavigationUrl)
        } else {
          window.location.href = this.pendingNavigationUrl
        }
      }
    }

    this.close()
  }

  /**
   * Save - submit form, then navigate if there's a pending URL
   */
  save(event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    if (!this.formElement) return

    // Always dispatch saving event before state is cleared by close().
    // This allows the management controller (which survives DOM replacement after save)
    // to capture the pending URL and handle post-save turbo-stream navigation.
    document.dispatchEvent(new CustomEvent("unsaved-changes:saving", {
      detail: {
        url: this.pendingNavigationUrl,
        turboStream: this.pendingIsTurboStream
      },
      bubbles: true
    }))

    if (this.pendingIsTurboStream) {
      // For turbo-stream pending navigations, the management controller handles
      // post-save navigation. Skip postSaveUrl to avoid double-navigation.
      this.close()
      this.formElement.requestSubmit()
      return
    }

    // Non-turbo-stream: use existing postSaveUrl mechanism in handleTurboSubmitEnd
    this.postSaveUrl = this.pendingNavigationUrl

    // Close the modal
    this.close()

    // Submit the form normally — Rails handles CSRF, method spoofing, and redirects.
    // handleTurboSubmitEnd will navigate to postSaveUrl on success.
    this.formElement.requestSubmit()
  }

  /**
   * After successful form save, navigate to postSaveUrl if set.
   */
  private handleTurboSubmitEnd(event: Event): void {
    if (!this.formElement) return

    const submitEvent = event as CustomEvent<{
      success: boolean
      formSubmission: { formElement: HTMLFormElement }
    }>

    // Only handle events from our own form
    if (submitEvent.detail?.formSubmission?.formElement !== this.formElement) return

    const url = this.postSaveUrl
    this.postSaveUrl = null

    if (!submitEvent.detail?.success || !url) return

    // Disable navigation interception so the visit goes through cleanly
    this.isNavigating = true
    this.removeAllTurboListeners()

    if (typeof window.Turbo !== "undefined" && window.Turbo.visit) {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  /**
   * Remove all Turbo-related listeners
   */
  private removeAllTurboListeners(): void {
    if (this.boundTurboVisitHandler) {
      document.removeEventListener("turbo:before-visit", this.boundTurboVisitHandler)
    }
    if (this.boundTurboFetchHandler) {
      document.removeEventListener("turbo:before-fetch-request", this.boundTurboFetchHandler)
    }
  }

  /**
   * Handle backdrop click - close modal
   */
  backdropClick(event: Event): void {
    // Only close if clicking directly on the overlay (backdrop), not the dialog content
    if (event.target === this.overlayTarget) {
      this.cancel(event)
    }
  }

  /**
   * Handle keyboard events
   */
  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.cancel()
    }
  }

  private addKeyboardListener(): void {
    if (this.boundKeyHandler) {
      document.addEventListener("keydown", this.boundKeyHandler)
    }
  }

  private removeKeyboardListener(): void {
    if (this.boundKeyHandler) {
      document.removeEventListener("keydown", this.boundKeyHandler)
    }
  }
}

// TypeScript declaration for Turbo global
declare global {
  interface Window {
    Turbo?: {
      visit: (url: string, options?: object) => void
      renderStreamMessage: (html: string) => void
    }
  }
}
